import { type ReactNode } from 'react';
import { RequiredLabel } from '@gonfalon/launchpad-experimental/RequiredLabel';
import type { TextFieldProps } from '@launchpad-ui/components';
import { Input, Text, TextField as LaunchpadTextField } from '@launchpad-ui/components';

import { FormFieldError } from './FormFieldError';
import {
  type BrowserExtensionControlProps,
  getDisableBrowserExtensionAttributes,
} from './getDisableBrowserExtensionAttributes';
import { useField } from './useField';

export type FormTextInputFieldProps = TextFieldProps &
  BrowserExtensionControlProps & {
    label?: string | ReactNode;
    description?: ReactNode;
    placeholder?: string;
    updateOnBlur?: boolean;
    variant?: 'default' | 'minimal';
    // Transform the value on every change before it is displayed and committed,
    // e.g. force-uppercase an identifier as the user types.
    format?: (value: string) => string;
  };

export function TextInputField({
  label,
  placeholder,
  className,
  description,
  isRequired,
  updateOnBlur,
  format,
  disableBrowserExtensions = true,
  ...props
}: FormTextInputFieldProps) {
  const { field, ...fieldProps } = useField<string>({ updateOnBlur, format, ...props });
  const meta = field.getMeta();

  return (
    <LaunchpadTextField className={className} name={field.name} {...props} {...fieldProps}>
      {label && <RequiredLabel isRequired={isRequired}>{label}</RequiredLabel>}
      {description && <Text slot="description">{description}</Text>}
      <Input
        placeholder={placeholder}
        data-test-id={`${field.name}-input`}
        variant={props.variant}
        {...getDisableBrowserExtensionAttributes(disableBrowserExtensions)}
      />
      <FormFieldError meta={meta} />
    </LaunchpadTextField>
  );
}
